import { useRef, useMemo, useCallback, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _ from "lodash"

import {
  DEFAULT_FILTER_VALUE,
  Q_SUB_CATEGORY_ID,
  Q_ORDINAL,
  Q_PARENT_CATEGORY_ID,
  Q_VENUE,
  GTM_VIEW_DIRECTORY_CATEGORY,
} from "constant"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useGetOccupants } from "providers/venue/modules/data/occupants/useGetOccupants"
import { useVenueController } from "hooks/venue/useVenueController"

import { useInfiniteGetOccupants } from "providers/venue/modules/data/occupants/useInfiniteGetOccupants"

import {
  createSortingConfigFeatureByName,
  getFeatureDetail,
  ILevelDetail,
} from "utils/feature"
import { pushDataLayer } from "utils/googletagmanager"
import { useRecommendedDirectory } from "hooks/sponsored-content/recommended"
import { useTranslation } from "providers/i18n"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { sortFeatureByLocaleKey } from "utils/sortFeatureByLocaleKey"
import dayjs from "dayjs"

const DEFAULT_LEVEL_OBJECT = {
  id: "all",
  name: "All Floors",
  value: DEFAULT_FILTER_VALUE,
}

export const useDirectory = () => {
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)

  const navigate = useNavigate()
  const {
    occupants: venueOccupant,
    levels,
    venues,
    isMultiVenueProject,
    isMultiOrdinalVenue,
    findFeatureById,
    dataLoaded,
    pageConfig,
  } = useVenue()

  const {
    i18n: { language: chosenLanguage },
  } = useTranslation()
  const [searchParams] = useSearchParams()
  const ref = useRef<HTMLElement>(null)

  const defaultVenueFilter = useMemo(
    () => locationVenueId || DEFAULT_FILTER_VALUE,
    [locationVenueId]
  )

  const { recommendedDirectories, recommendedDirectoryConfig } =
    useRecommendedDirectory()

  // Query String filters
  const parentCategoryId = useMemo(
    () => searchParams.get(Q_PARENT_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const subCategoryId = useMemo(
    () => searchParams.get(Q_SUB_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const ordinalFilter = useMemo(() => {
    const ordinalParam = searchParams.get(Q_ORDINAL)
    const ordinal = Number(ordinalParam)
    return !_.isNil(ordinalParam) && !_.isNaN(ordinal)
      ? ordinal
      : DEFAULT_FILTER_VALUE
  }, [searchParams])

  // Filter occupants with an available anchor
  const { data: availableOccupants } = useGetOccupants({
    pagination: {
      perPage: _.size(venueOccupant),
    },
    filter: {
      properties: {
        anchor: {
          //@ts-ignore
          $exists: true,
        },
      },
    },
    enabled: dataLoaded,
  })

  // Filter occupants with a parentCategoryId.
  const { data: occupantsFilteredByParentCategory } = useGetOccupants({
    pagination: {
      perPage: _.size(availableOccupants),
    },
    filter: {
      properties: {
        //@ts-ignore
        local_parent_categories: {
          $elemMatch: {
            id: parentCategoryId,
          },
        },
      },
    },
    enabled: !!availableOccupants,
  })

  /**
   * Result All Filtered Occupants (without ordinal filter)
   * - Used to create an array of unique levels of occupants that match the category
   * - Used as the source data for the useInfiniteGetOccupants hook
   */
  const { data: allfilteredOccupantsSorted } = useGetOccupants({
    pagination: {
      perPage: _.size(availableOccupants),
    },
    sort: createSortingConfigFeatureByName(chosenLanguage),
    filter: {
      properties: {
        local_parent_categories: {
          $elemMatch: {
            id: parentCategoryId,
          },
        },
        local_categories: {
          //@ts-ignore
          $elemMatch: {
            id: subCategoryId,
          },
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
      },
    },
    enabled: !!occupantsFilteredByParentCategory,
  })

  /**
   * Result Infinite Filtered Occupants
   * - Used to display data in an infinite scroll version
   */
  const {
    data: filteredOccupantsSorted,
    hasNextPage,
    isFetched,
    fetchNextPage,
  } = useInfiniteGetOccupants({
    pagination: {
      perPage: 25,
    },
    sort: createSortingConfigFeatureByName(chosenLanguage),
    filter: {
      properties: {
        local_parent_categories: {
          $elemMatch: {
            id: parentCategoryId,
          },
        },
        local_categories: {
          //@ts-ignore
          $elemMatch: {
            id: subCategoryId,
          },
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
        //@ts-ignore
        ordinal: ordinalFilter,
      },
    },
    enabled: !!allfilteredOccupantsSorted,
  })

  //check isNewShop and isComingSoon
  const processedOccupantData = useMemo(() => {
    if (_.isEmpty(filteredOccupantsSorted?.pages))
      return filteredOccupantsSorted

    const currentDate = dayjs()
    const periodInDays = _.get(pageConfig, "newShop.metadata.periodInDays", 30)

    return {
      pages: filteredOccupantsSorted.pages.map((page) => ({
        ...page,
        data: page.data.map((occupant) => {
          if (!occupant?.properties?.start_date) {
            return occupant
          }

          const startDate = dayjs(occupant.properties.start_date)
          const isNewShop =
            currentDate.isSameOrAfter(startDate) &&
            currentDate.diff(startDate, "day") <= periodInDays

          const isComingSoon = startDate.isAfter(currentDate)

          return {
            ...occupant,
            properties: {
              ...occupant.properties,
              isNewShop,
              isComingSoon,
            },
          }
        }),
      })),
      pageParams: filteredOccupantsSorted.pageParams,
    }
  }, [pageConfig, filteredOccupantsSorted])

  // every xxx record has 1 sponsored-content
  const sponsoredContentFrequency = useMemo(
    () => _.get(recommendedDirectoryConfig, "frequency", 2),
    [recommendedDirectoryConfig]
  )

  const attachedAdsResult = useMemo(() => {
    if (
      _.isEmpty(processedOccupantData?.pages) ||
      _.isEmpty(recommendedDirectories)
    )
      return processedOccupantData

    // frequency <= 0 means "pin all sponsored content to the top"
    if (sponsoredContentFrequency <= 0) {
      return {
        pages: processedOccupantData.pages.map((page, pageIndex) => ({
          ...page,
          data:
            pageIndex === 0
              ? [...recommendedDirectories, ...(page.data || [])]
              : page.data,
        })),
        pageParams: processedOccupantData.pageParams,
      }
    }

    let adIndex = 0
    const insertAdsIntoResult = (data) => {
      return data.flatMap((chunk) =>
        adIndex < recommendedDirectories.length
          ? chunk.concat(recommendedDirectories[adIndex++])
          : chunk
      )
    }
    return {
      pages: processedOccupantData.pages.map((page) => ({
        ...page,
        data: insertAdsIntoResult(
          _.chunk(page.data || [], sponsoredContentFrequency)
        ),
      })),
      pageParams: processedOccupantData.pageParams,
    }
  }, [processedOccupantData, recommendedDirectories, sponsoredContentFrequency])

  const onReachEndScroll = useCallback(
    (inView) => {
      if (inView && hasNextPage) fetchNextPage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasNextPage]
  )

  const { endScrollRef } = useInfiniteScroll({
    onReachEndScroll,
  })

  // Prepare parent category by available occupants
  const parentCategoryWithOccupant = useMemo(
    () =>
      _(availableOccupants)
        .map("properties.local_parent_categories")
        .compact()
        .flatten()
        .uniqBy("id")
        .value(),
    [availableOccupants]
  )

  const sortedParentCategoryWithOccupant = useMemo(
    () =>
      _.orderBy(
        parentCategoryWithOccupant,
        ["properties.is_featured", "properties.order"],
        ["desc", "asc"]
      ),
    [parentCategoryWithOccupant]
  )

  // Prepare subcategories by filtering parent category occupants.
  const subCategoryWithActiveParentCategory = useMemo(
    () =>
      _.isNil(parentCategoryId)
        ? []
        : _(occupantsFilteredByParentCategory)
            .map("properties.local_categories")
            .compact()
            .flatten()
            .uniqBy("id")
            .filter(
              (category) =>
                _.get(category, "properties.parent_id") === parentCategoryId
            )
            .sortBy("properties.order")
            .value(),
    [parentCategoryId, occupantsFilteredByParentCategory]
  )

  // Prepare venue options.
  const sortedVenues = useMemo(() => {
    return sortFeatureByLocaleKey(venues, "properties.name", chosenLanguage)
  }, [chosenLanguage, venues])

  const venueOptions = useMemo(() => {
    const defaultVenueObject = {
      id: "all-venues",
      name: "All Venues",
      value: DEFAULT_FILTER_VALUE,
    }
    const options = sortedVenues.map((venue) => {
      const { id, name } = getFeatureDetail(venue)
      return { id, name, value: id }
    })

    return [defaultVenueObject, ...options]
  }, [sortedVenues])

  // Array of unique levels of occupants who matched with category
  const filteredLevels = useMemo(
    () =>
      _(allfilteredOccupantsSorted)
        .uniqBy("properties.ordinal")
        .filter((item) => !_.isNil(item.properties.ordinal))
        .map(({ properties: { ordinal } }) =>
          levels.find((level) => level.properties.ordinal === ordinal)
        )
        .orderBy("properties.ordinal", "asc")
        .value(),
    [levels, allfilteredOccupantsSorted]
  )

  const levelOptions = useMemo(() => {
    const options = filteredLevels.map((level) => {
      const { id, name, ordinal } = getFeatureDetail(level) as ILevelDetail
      return { id, name, value: ordinal }
    })

    return [DEFAULT_LEVEL_OBJECT, ...options]
  }, [filteredLevels])

  const hasData = _.size(filteredOccupantsSorted?.pages[0]?.data) > 0
  const hasParentCategory = _.size(sortedParentCategoryWithOccupant) > 0

  const scrollToRef = useCallback(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [ref])

  const handleChangeOrdinal = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_ORDINAL, id)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
      scrollToRef()
    },
    [scrollToRef, searchParams, navigate]
  )

  const changeVenueFilter = useCallback(
    (venueId = defaultVenueFilter) => {
      searchParams.set(Q_VENUE, venueId)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
      scrollToRef()
    },
    [scrollToRef, searchParams, navigate, defaultVenueFilter]
  )

  const handleSelectLevelChange = useCallback(
    (e) => {
      const ordinal = e.target.value
      handleChangeOrdinal(e, ordinal)
    },
    [handleChangeOrdinal]
  )

  const handleChangeParentCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_PARENT_CATEGORY_ID, id)
      searchParams.set(Q_SUB_CATEGORY_ID, DEFAULT_FILTER_VALUE)
      searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleChangeSubCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_SUB_CATEGORY_ID, id)
      searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const resetFilter = useCallback(() => {
    searchParams.set(Q_PARENT_CATEGORY_ID, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_SUB_CATEGORY_ID, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_VENUE, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
    navigate(`/directory?${searchParams.toString()}`, { replace: true })
  }, [navigate, searchParams])

  const showSubCategoryFilter = useMemo(
    () =>
      subCategoryWithActiveParentCategory.length > 0 &&
      parentCategoryId !== DEFAULT_FILTER_VALUE,
    [subCategoryWithActiveParentCategory, parentCategoryId]
  )

  useEffect(() => {
    try {
      if (!!parentCategoryId && parentCategoryId !== DEFAULT_FILTER_VALUE) {
        const categoryName = parentCategoryWithOccupant.find(
          (taxonomy) => taxonomy.id === parentCategoryId
        )?.properties?.name?.en
        return pushDataLayer({
          event: GTM_VIEW_DIRECTORY_CATEGORY,
          directory_category_id: parentCategoryId,
          directory_category_name: categoryName,
          directory_subcategory_name:
            subCategoryId === DEFAULT_FILTER_VALUE
              ? DEFAULT_FILTER_VALUE
              : _.get(findFeatureById(subCategoryId), "properties.name.en"),
        })
      }
    } catch (err) {
      console.log(err)
    }
  }, [
    parentCategoryId,
    parentCategoryWithOccupant,
    subCategoryId,
    findFeatureById,
  ])

  //MainCategory Data
  const parentCategory = useMemo(
    () =>
      parentCategoryId === DEFAULT_FILTER_VALUE
        ? null
        : parentCategoryWithOccupant.find(
            (taxonomy) => taxonomy.id === parentCategoryId
          ) || null,
    [parentCategoryId, parentCategoryWithOccupant]
  )

  return {
    handleChangeParentCategory,
    handleChangeSubCategory,
    handleChangeOrdinal,
    handleSelectLevelChange,
    changeVenueFilter,
    resetFilter,
    endScrollRef,
    hasNextPage,
    filteredOccupantsSorted: attachedAdsResult,
    filteredLevels,
    parentCategoryWithOccupant,
    sortedParentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    ordinalFilter,
    venueFilter,
    levelOptions,
    venueOptions,
    defaultOrdinalFilterValue: DEFAULT_FILTER_VALUE,
    defaultCategoryFilterValue: DEFAULT_FILTER_VALUE,
    defaultVenueFilterValue: DEFAULT_FILTER_VALUE,
    ref,
    venues,
    isMultiVenueProject,
    isMultiOrdinalVenue,
    locationVenueId,
    showSubCategoryFilter,
    hasData,
    isFetched,
    hasParentCategory,
    parentCategory,
  }
}
